import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useAnalytics} from '@github-ui/use-analytics'
import {type ColorName, isColorName, useNamedColor} from '@github-ui/use-named-color'
import {CheckIcon, KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, AnchoredOverlay, Button, IconButton, Label, Link} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {
  type ApplySuggestionRef,
  commitApplyPendingIssueSuggestionsMutation,
} from '../../mutations/apply-pending-issue-suggestions-mutation'
import {
  commitRejectPendingIssueSuggestionsMutation,
  type RejectSuggestionRef,
} from '../../mutations/reject-pending-issue-suggestions-mutation'
import type {useRecentAgentActivity_issue$key} from './__generated__/useRecentAgentActivity_issue.graphql'
import styles from './RecentAgentActivity.module.css'
import {useDismissRecentAgentActivity} from './use-dismiss-recent-agent-activity'
import type {AgentAction, PendingSuggestion, SuggestionConfidence} from './use-recent-agent-activity'
import {computeRecentActivitySince, useRecentAgentActivity} from './use-recent-agent-activity'
import {useRecentAgentActivitySubscription} from './use-recent-agent-activity-subscription'

function resolveDotColor(color: string | null | undefined): string | undefined {
  if (!color) return undefined
  // Hex string with or without leading '#'
  if (/^#?[0-9a-f]{3,8}$/i.test(color)) {
    return color.startsWith('#') ? color : `#${color}`
  }
  return undefined
}

type ChipValueProps = {
  name: string
  color?: string | null
}

function ChipValue({name, color}: ChipValueProps) {
  const isNamed = Boolean(color && isColorName(color))
  const {accent} = useNamedColor(isNamed ? (color as ColorName) : undefined)
  const resolved = isNamed ? accent : resolveDotColor(color)
  return (
    <span className={styles.chipValueInner}>
      {resolved && <span className={styles.chipDot} style={{backgroundColor: resolved}} aria-hidden="true" />}
      <span className={styles.chipValueText}>{name}</span>
    </span>
  )
}

type SuggestionChipProps = {
  fieldName: string
  applied: boolean
  rationale?: string | null
  ariaLabel: string
  confidence?: SuggestionConfidence | null
  onApprove?: () => void
  onReject?: () => void
  children: ReactNode
  wrapperClassName?: string
  onWrapperTransitionEnd?: (event: React.TransitionEvent<HTMLDivElement>) => void
}

const CONFIDENCE_LABELS: Record<SuggestionConfidence, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
}

const CONFIDENCE_LABEL_VARIANTS: Record<SuggestionConfidence, 'success' | 'accent' | 'done'> = {
  HIGH: 'success',
  MEDIUM: 'accent',
  LOW: 'done',
}

function SuggestionChip({
  fieldName,
  applied,
  rationale,
  ariaLabel,
  confidence,
  onApprove,
  onReject,
  children,
  wrapperClassName,
  onWrapperTransitionEnd,
}: SuggestionChipProps) {
  const [open, setOpen] = useState(false)
  const {sendAnalyticsEvent} = useAnalytics()

  const interactive = Boolean(onApprove || onReject || rationale)

  const handleOpen = useCallback(() => {
    setOpen(true)
    if (rationale) {
      sendAnalyticsEvent('recent_agent_actions_panel.rationale_clicked', 'RECENT_AGENT_ACTIVITY', {
        rationaleLength: rationale.length,
      })
    }
  }, [rationale, sendAnalyticsEvent])

  const handleClose = useCallback(() => setOpen(false), [])

  const handleApproveClick = useCallback(() => {
    setOpen(false)
    onApprove?.()
  }, [onApprove])

  const handleRejectClick = useCallback(() => {
    setOpen(false)
    onReject?.()
  }, [onReject])

  const chipContent = (
    <>
      <span className={styles.chipFieldName}>{fieldName}</span>
      <span className={styles.chipArrow} aria-hidden="true">
        →
      </span>
      <span className={styles.chipValue}>{children}</span>
      {applied && <CheckIcon size={12} className={styles.chipCheck} aria-hidden="true" />}
    </>
  )

  if (!interactive) {
    return (
      <div
        className={clsx(styles.chip, applied && styles.chipApplied)}
        data-confidence={confidence ?? undefined}
        role="listitem"
      >
        {chipContent}
      </div>
    )
  }

  return (
    <div
      role="listitem"
      className={clsx(styles.chipWrapper, wrapperClassName)}
      onTransitionEnd={onWrapperTransitionEnd}
    >
      <AnchoredOverlay
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        renderAnchor={anchorProps => (
          <button
            type="button"
            {...anchorProps}
            className={clsx(styles.chip, styles.chipButton, applied && styles.chipApplied)}
            data-confidence={confidence ?? undefined}
            aria-label={ariaLabel}
          >
            {chipContent}
          </button>
        )}
      >
        <div className={styles.rationaleOverlay}>
          {confidence && (
            <Label variant={CONFIDENCE_LABEL_VARIANTS[confidence]} className={styles.confidenceLabel}>
              {`${CONFIDENCE_LABELS[confidence]} confidence`}
            </Label>
          )}
          {rationale && <div className={styles.rationaleBody}>{rationale}</div>}
          {(onApprove || onReject) && (
            <div className={styles.rationaleActions}>
              {onReject && (
                <Button variant="default" size="small" onClick={handleRejectClick}>
                  Reject
                </Button>
              )}
              {onApprove && (
                <Button variant="primary" size="small" onClick={handleApproveClick}>
                  Approve
                </Button>
              )}
            </div>
          )}
        </div>
      </AnchoredOverlay>
    </div>
  )
}

type AppliedActionChipsProps = {
  action: AgentAction
}

const AppliedActionChips = memo(function AppliedActionChips({action}: AppliedActionChipsProps) {
  if (action.labels && action.labels.length > 0) {
    const labelNames = action.labels.map(l => l.name).join(', ')
    return (
      <SuggestionChip
        fieldName={action.fieldName}
        applied
        rationale={action.rationale}
        ariaLabel={`Applied ${action.fieldName}: ${labelNames}${action.rationale ? '. View rationale' : ''}`}
      >
        {action.labels.map((label, index) => (
          <span key={`label-${label.name}`} className={styles.chipValueGroup}>
            {index > 0 && (
              <span className={styles.chipValueSeparator} aria-hidden="true">
                ,
              </span>
            )}
            <ChipValue name={label.name} color={label.color} />
          </span>
        ))}
      </SuggestionChip>
    )
  }

  if (action.value) {
    return (
      <SuggestionChip
        fieldName={action.fieldName}
        applied
        rationale={action.rationale}
        ariaLabel={`Applied ${action.fieldName}: ${action.value}${action.rationale ? '. View rationale' : ''}`}
      >
        <ChipValue name={action.value} color={action.color} />
      </SuggestionChip>
    )
  }

  return (
    <SuggestionChip
      fieldName={action.fieldName}
      applied
      rationale={action.rationale}
      ariaLabel={`Applied ${action.fieldName}${action.rationale ? '. View rationale' : ''}`}
    >
      <ChipValue name="" />
    </SuggestionChip>
  )
})

type PendingSuggestionChipProps = {
  suggestion: PendingSuggestion
  onApprove: (suggestion: PendingSuggestion) => void
  onReject: (suggestion: PendingSuggestion) => void
}

const REJECT_ANIMATION_FALLBACK_MS = 400

const PendingSuggestionChip = memo(function PendingSuggestionChip({
  suggestion,
  onApprove,
  onReject,
}: PendingSuggestionChipProps) {
  const [isRejecting, setIsRejecting] = useState(false)
  const hasFiredRejectRef = useRef(false)
  const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fireReject = useCallback(() => {
    if (hasFiredRejectRef.current) return
    hasFiredRejectRef.current = true
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current)
      fallbackTimeoutRef.current = null
    }
    onReject(suggestion)
  }, [onReject, suggestion])

  useEffect(
    () => () => {
      if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current)
    },
    [],
  )

  const handleApprove = useCallback(() => onApprove(suggestion), [onApprove, suggestion])
  const handleReject = useCallback(() => {
    setIsRejecting(true)
    // The mutation is normally fired from transitionEnd so the chip animates
    // out first. transitionEnd is not guaranteed to fire (reduced motion,
    // backgrounded tabs, rapid DOM changes), so add a timeout fallback that
    // still fires the reject if the event never arrives.
    fallbackTimeoutRef.current = setTimeout(fireReject, REJECT_ANIMATION_FALLBACK_MS)
  }, [fireReject])

  const handleTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (isRejecting && event.propertyName === 'opacity' && event.target === event.currentTarget) {
        fireReject()
      }
    },
    [fireReject, isRejecting],
  )

  return (
    <SuggestionChip
      fieldName={suggestion.displayFieldName}
      applied={false}
      rationale={suggestion.rationale}
      confidence={suggestion.confidence}
      ariaLabel={`Pending ${suggestion.displayFieldName} suggestion${suggestion.value ? `: ${suggestion.value}` : ''}${suggestion.confidence ? `. ${CONFIDENCE_LABELS[suggestion.confidence]} confidence` : ''}. View details`}
      onApprove={handleApprove}
      onReject={handleReject}
      wrapperClassName={isRejecting ? styles.chipRejecting : undefined}
      onWrapperTransitionEnd={handleTransitionEnd}
    >
      <ChipValue name={suggestion.value ?? ''} color={suggestion.color} />
    </SuggestionChip>
  )
})

type RecentAgentActivityProps = {
  issue: useRecentAgentActivity_issue$key
}

const FEEDBACK_URL = 'https://github.com/orgs/community/discussions/198815'

function buildApplyRef(suggestion: PendingSuggestion): ApplySuggestionRef | null {
  const baseOptimisticEvent = {
    actorTypename: suggestion.actorTypename,
    actorLogin: suggestion.actorLogin,
    actorAvatarUrl: suggestion.actorAvatarUrl,
    actorDisplayName: suggestion.actorDisplayName,
    rationale: suggestion.rationale,
  }
  if (suggestion.type === 'type_set' && suggestion.issueTypeId && suggestion.issueTypeName) {
    return {
      kind: 'TYPE',
      issueTypeId: suggestion.issueTypeId,
      optimisticEvent: {
        ...baseOptimisticEvent,
        issueTypeName: suggestion.issueTypeName,
        issueTypeColor: suggestion.issueTypeColor,
      },
    }
  }
  if (suggestion.type === 'label_added' && suggestion.labelId && suggestion.value) {
    return {
      kind: 'LABEL',
      labelId: suggestion.labelId,
      optimisticEvent: {
        ...baseOptimisticEvent,
        labelName: suggestion.value,
        labelColor: suggestion.color,
      },
    }
  }
  if (suggestion.type === 'field_set' && suggestion.issueFieldId) {
    return {
      kind: 'FIELD',
      issueFieldId: suggestion.issueFieldId,
      optimisticEvent: {
        ...baseOptimisticEvent,
        fieldName: suggestion.displayFieldName,
        value: suggestion.value,
        color: suggestion.color,
      },
    }
  }
  return null
}

function buildRejectRef(suggestion: PendingSuggestion): RejectSuggestionRef | null {
  if (suggestion.type === 'type_set' && suggestion.issueTypeId) {
    return {kind: 'TYPE', issueTypeId: suggestion.issueTypeId}
  }
  if (suggestion.type === 'label_added' && suggestion.labelId) {
    return {kind: 'LABEL', labelId: suggestion.labelId}
  }
  if (suggestion.type === 'field_set' && suggestion.issueFieldId) {
    return {kind: 'FIELD', issueFieldId: suggestion.issueFieldId}
  }
  return null
}

function countByConfidence(suggestions: PendingSuggestion[]): {
  high_confidence_count: number
  medium_confidence_count: number
  low_confidence_count: number
  no_confidence_count: number
} {
  const counts = {high_confidence_count: 0, medium_confidence_count: 0, low_confidence_count: 0, no_confidence_count: 0}
  for (const suggestion of suggestions) {
    switch (suggestion.confidence) {
      case 'HIGH':
        counts.high_confidence_count++
        break
      case 'MEDIUM':
        counts.medium_confidence_count++
        break
      case 'LOW':
        counts.low_confidence_count++
        break
      default:
        counts.no_confidence_count++
    }
  }
  return counts
}

export function RecentAgentActivity({issue}: RecentAgentActivityProps) {
  const {hasRecentActivity, groups, totalActionCount, viewerCanUpdateMetadata, issueId, pendingSuggestions} =
    useRecentAgentActivity(issue)
  const {isDismissed, dismissForIssue, dismissForever} = useDismissRecentAgentActivity(issueId)
  const [recentActivitySince] = useState(computeRecentActivitySince)
  const {sendAnalyticsEvent} = useAnalytics()
  const environment = useRelayEnvironment()

  useRecentAgentActivitySubscription(issueId, recentActivitySince)

  const pendingSuggestionRows = useMemo(
    () => pendingSuggestions.groups.flatMap(group => group.suggestions.filter(s => s.state === 'PENDING')),
    [pendingSuggestions.groups],
  )
  const pendingCount = pendingSuggestionRows.length

  const panelRef = useRef<HTMLDivElement | null>(null)

  // After a chip is approved or rejected (individually or in bulk), move focus
  // to the next focusable chip in the list. If none remain, fall back to the
  // kebab "More actions" button so focus doesn't fall to document.body.
  const focusAfterAction = useCallback(() => {
    // Defer two frames so React can unmount the dismissed chip and any
    // optimistic store update can land.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const panel = panelRef.current
        if (!panel) return
        const nextChip = panel.querySelector<HTMLButtonElement>('[role="list"] button')
        if (nextChip) {
          nextChip.focus()
          return
        }
        const fallback = panel.querySelector<HTMLButtonElement>('[aria-label="More actions"]')
        fallback?.focus()
      })
    })
  }, [])

  const handleApprove = useCallback(
    (suggestion: PendingSuggestion) => {
      sendAnalyticsEvent('issues.recent_agent_activity.suggestion_approved', 'RECENT_AGENT_ACTIVITY', {
        suggestion_type: suggestion.type,
        confidence: suggestion.confidence ?? 'NONE',
      })
      const ref = buildApplyRef(suggestion)
      if (!ref) return
      commitApplyPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId: suggestion.actorId,
        suggestions: [ref],
        since: recentActivitySince,
      })
      announce('Suggestion approved')
      focusAfterAction()
    },
    [environment, issueId, recentActivitySince, sendAnalyticsEvent, focusAfterAction],
  )

  const handleReject = useCallback(
    (suggestion: PendingSuggestion) => {
      sendAnalyticsEvent('issues.recent_agent_activity.suggestion_rejected', 'RECENT_AGENT_ACTIVITY', {
        suggestion_type: suggestion.type,
        confidence: suggestion.confidence ?? 'NONE',
      })
      const ref = buildRejectRef(suggestion)
      if (!ref) return
      commitRejectPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId: suggestion.actorId,
        suggestions: [ref],
      })
      announce('Suggestion rejected')
      focusAfterAction()
    },
    [environment, issueId, sendAnalyticsEvent, focusAfterAction],
  )

  const handleApproveAll = useCallback(() => {
    sendAnalyticsEvent('issues.recent_agent_activity.suggestion_bulk_approved', 'RECENT_AGENT_ACTIVITY', {
      suggestion_count: pendingSuggestionRows.length,
      ...countByConfidence(pendingSuggestionRows),
    })
    // The unified mutation requires a single actorId per call; batch suggestions per actor.
    const byActor = new Map<string, {refs: ApplySuggestionRef[]}>()
    for (const suggestion of pendingSuggestionRows) {
      const ref = buildApplyRef(suggestion)
      if (!ref) continue
      let bucket = byActor.get(suggestion.actorId)
      if (!bucket) {
        bucket = {refs: []}
        byActor.set(suggestion.actorId, bucket)
      }
      bucket.refs.push(ref)
    }
    for (const [actorId, {refs}] of byActor) {
      commitApplyPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId,
        suggestions: refs,
        since: recentActivitySince,
      })
    }
    const count = pendingSuggestionRows.length
    announce(count === 1 ? 'Suggestion approved' : `${count} suggestions approved`)
    focusAfterAction()
  }, [pendingSuggestionRows, environment, issueId, recentActivitySince, sendAnalyticsEvent, focusAfterAction])

  const handleRejectAll = useCallback(() => {
    sendAnalyticsEvent('issues.recent_agent_activity.suggestion_bulk_rejected', 'RECENT_AGENT_ACTIVITY', {
      suggestion_count: pendingSuggestionRows.length,
      ...countByConfidence(pendingSuggestionRows),
    })
    // The unified mutation requires a single actorId per call; batch suggestions per actor.
    const byActor = new Map<string, {refs: RejectSuggestionRef[]}>()
    for (const suggestion of pendingSuggestionRows) {
      const ref = buildRejectRef(suggestion)
      if (!ref) continue
      let bucket = byActor.get(suggestion.actorId)
      if (!bucket) {
        bucket = {refs: []}
        byActor.set(suggestion.actorId, bucket)
      }
      bucket.refs.push(ref)
    }
    for (const [actorId, {refs}] of byActor) {
      commitRejectPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId,
        suggestions: refs,
      })
    }
    const count = pendingSuggestionRows.length
    announce(count === 1 ? 'Suggestion rejected' : `${count} suggestions rejected`)
    focusAfterAction()
  }, [pendingSuggestionRows, environment, issueId, sendAnalyticsEvent, focusAfterAction])

  const hasFiredDisplayedRef = useRef(false)
  useEffect(() => {
    if (hasRecentActivity && viewerCanUpdateMetadata && !isDismissed && !hasFiredDisplayedRef.current) {
      hasFiredDisplayedRef.current = true
      sendAnalyticsEvent('recent_agent_actions_panel.displayed', 'RECENT_AGENT_ACTIVITY', {
        action_count: totalActionCount,
        agent_count: groups.length,
      })
    }
  }, [hasRecentActivity, viewerCanUpdateMetadata, isDismissed, sendAnalyticsEvent, totalActionCount, groups.length])

  if ((!hasRecentActivity && pendingCount === 0) || !viewerCanUpdateMetadata || isDismissed) {
    return null
  }

  const primaryGroup = groups[0] ?? {
    agentName: pendingSuggestions.groups[0]?.agentName ?? '',
    agentSlug: pendingSuggestions.groups[0]?.agentSlug ?? '',
    actorLogin: pendingSuggestions.groups[0]?.actorLogin ?? '',
    actorAvatarUrl: pendingSuggestions.groups[0]?.actorAvatarUrl ?? '',
    actions: [],
  }
  if (!primaryGroup.agentSlug) return null

  const hasApplied = totalActionCount > 0
  const hasPending = pendingCount > 0

  return (
    <div
      ref={panelRef}
      className={clsx(styles.container, isFeatureEnabled('issue_inline_avatars') && styles.container_inlineAvatars)}
      data-testid="recent-agent-activity"
    >
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h3 className={styles.title}>Agent actions</h3>
          <Link href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer" className={styles.previewLink}>
            <Label variant="secondary">Private preview</Label>
          </Link>
        </div>
        <div className={styles.headerActions}>
          {hasPending && (
            <>
              <Button variant="default" size="small" onClick={handleRejectAll}>
                Reject all
              </Button>
              <Button variant="primary" size="small" onClick={handleApproveAll}>
                Approve all
              </Button>
            </>
          )}
          <ActionMenu>
            <ActionMenu.Anchor>
              <IconButton icon={KebabHorizontalIcon} aria-label="More actions" size="small" variant="invisible" />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay>
              <ActionList>
                <ActionList.LinkItem href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
                  Give feedback
                </ActionList.LinkItem>
                <ActionList.Item onSelect={dismissForIssue}>Dismiss</ActionList.Item>
                <ActionList.Item onSelect={dismissForever}>Never show this again</ActionList.Item>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </div>
      </div>
      <div className={styles.chips} role="list" aria-label="Agent actions">
        {pendingSuggestionRows.map(suggestion => {
          const subjectKey =
            suggestion.issueTypeId ??
            suggestion.labelId ??
            suggestion.issueFieldId ??
            `${suggestion.type}:${suggestion.displayFieldName}`
          return (
            <PendingSuggestionChip
              key={`pending-${suggestion.actorId}:${subjectKey}`}
              suggestion={suggestion}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          )
        })}
        {hasPending && hasApplied && <span className={styles.divider} aria-hidden="true" />}
        {groups.flatMap(group =>
          group.actions.map(action => (
            <AppliedActionChips
              key={`${group.agentSlug}-${action.fieldName}-${action.value}-${action.createdAt}`}
              action={action}
            />
          )),
        )}
      </div>
    </div>
  )
}
